import type { MemoNote } from '@seen/core/models/memoNote'
import type { Participant } from '@seen/core/models/patients/participant'
import { Button } from '@seen/ui'
import { IconHistory } from '@tabler/icons-react'
import { type JSX, useCallback, useState } from 'react'
import { toast } from 'sonner'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { MemoEditor } from '#src/components/patient/detail-content/memo-tab/MemoEditor.tsx'
import { VersionHistoryDialog } from '#src/components/VersionHistoryDialog.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMemoNote } from '#src/hooks/useMemoNote.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

export function MemoTab({
  participant,
}: {
  participant: Participant
}): JSX.Element {
  const { memo, isLoading, needsCreation, createMemo } = useMemoNote(
    participant.reference
  )
  const isReadOnly = useIsReadOnly()
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)
  const { mutate: patchMemo } = usePatchMutation<MemoNote>()

  const handleRestoreVersion = useCallback(
    (version: MemoNote) => {
      if (!memo) {
        return
      }

      patchMemo({
        instance: memo,
        mutateFn: (s) => {
          s.content = version.content
        },
        patchOptions: {
          skipVersionCheck: true,
        },
      })
      toast.success('Version restored')
    },
    [memo, patchMemo]
  )

  if (isLoading) {
    return <Loader />
  }

  if (needsCreation) {
    if (isReadOnly) {
      return <p className="text-sm text-foreground-secondary">No memo yet.</p>
    }
    return (
      <div className="flex flex-col items-center gap-2 py-4">
        <Button variant="outline" size="sm" onClick={createMemo}>
          Start memo
        </Button>
      </div>
    )
  }

  if (!memo) {
    return <Loader />
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col text-sm">
      <div className="flex items-center justify-between py-1.5">
        <h5>Memo</h5>
        <MoreDropdownMenu>
          <DropdownMenuItem onSelect={() => setIsVersionHistoryOpen(true)}>
            <IconHistory className="size-4" />
            Version history
          </DropdownMenuItem>
          <DropdownMenuMedplumLinkItem to={memo} />
        </MoreDropdownMenu>
      </div>
      <MemoEditor key={memo.versionId} memo={memo} />
      <VersionHistoryDialog
        open={isVersionHistoryOpen}
        onOpenChange={setIsVersionHistoryOpen}
        resource={memo}
        onRestoreVersion={handleRestoreVersion}
      />
    </div>
  )
}
